import { Component, Show } from "solid-js";
import { Button } from "../ui";
import { DegradedOutputBadge } from "../quota/DegradedOutputBadge";
import type { QuotaScope, RelevanceSummary } from "../../api";

interface AutoPrioritizePreviewBarProps {
  movedCount: number;
  applying: boolean;
  onApply: () => void;
  onCancel: () => void;
  /** The proposal came from the quota fallback tier (FLOW-794). */
  degraded: boolean;
  /** Which ceiling bound it to that tier, when the backend named one (FLOW-930). */
  degradedScope?: QuotaScope;
  /** Discard the preview and run auto-prioritize again. */
  onRegenerate: () => void;
  /** Focus the run was scoped to (FLOW-920); omitted when none was used. */
  focus?: string | null;
  /** What the relevance filter kept (FLOW-921); omitted when no filter ran. */
  relevance?: RelevanceSummary | null;
}

export const AutoPrioritizePreviewBar: Component<
  AutoPrioritizePreviewBarProps
> = (props) => {
  const focusLabel = () => props.focus?.trim() || null;
  const relevanceLine = () => {
    const r = props.relevance;
    if (!r || r.total === 0) return null;
    if (r.scored === 0)
      return `${r.relevant} of ${r.total} items matched this focus, but none could be scored — the order is unchanged.`;
    if (r.scored === r.total)
      return `${r.scored} of ${r.total} items were scored for this focus and moved to the top.`;
    return `${r.scored} of ${r.total} items were scored for this focus and moved to the top; the other ${r.total - r.scored} kept their relative order below.`;
  };

  return (
    <div class="sticky top-0 z-10 flex items-center justify-between gap-3 px-6 py-2 bg-signal-magenta-tint border-b border-signal-magenta/25 text-ink">
      <div class="min-w-0 flex flex-col gap-0.5 text-sm">
        <div class="flex items-center gap-1.5">
          {/* `role="status"` wraps only the announced text — the badge
              beside it opens a `role="dialog"` popover, which must not be
              swept into this live region's announcement. */}
          <div role="status" class="flex items-center gap-1.5">
            <span class="font-mono text-[9px] uppercase tracking-[0.12em] px-1 rounded-sm border border-signal-magenta/25 text-signal-magenta">
              ai
            </span>{" "}
            <span class="font-medium">Preview</span>{" "}
            <span aria-hidden="true">·</span>{" "}
            <span class="font-mono tabular-nums">{props.movedCount}</span>{" "}
            <span>
              items reordered. Drag-and-drop is paused while you review.
            </span>
          </div>
          <Show when={props.degraded}>
            <DegradedOutputBadge
              onRegenerate={() => props.onRegenerate()}
              scope={props.degradedScope}
            />
          </Show>
        </div>
        <Show when={focusLabel()}>
          {(focus) => (
            <span class="text-xs text-ink-2 line-clamp-1" title={focus()}>
              Prioritized for: {focus()}
            </span>
          )}
        </Show>
        <Show when={relevanceLine()}>
          {(line) => <span class="text-xs text-ink-2">{line()}</span>}
        </Show>
      </div>
      <div class="flex gap-2 shrink-0">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => props.onCancel()}
          disabled={props.applying}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => props.onApply()}
          disabled={props.applying}
        >
          {props.applying ? "Applying…" : "Apply changes"}
        </Button>
      </div>
    </div>
  );
};
